import {
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragCancelEvent,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';

import type {EagGridStore} from '../store/createEagGridStore';
import type {EagColumnState} from '../types/EagColumnState';
import {isGroupRowCellId, type GroupRowCell, type GroupRowModel} from '../utils/groupRowModel';

const DRAG_ACTIVATION_DISTANCE = 8;

// Drag visuals are applied via a `<style>` element scoped by the `data-column`
// attribute on each `<td>`. This keeps the column-drag hot path off the React
// reconciler — cells do not subscribe to drag state and never re-render during
// a drag (verified via per-render counter: 10,050 → 0 cell renders).
//
// Colors mirror the Tailwind classes the JSX used previously:
//   #eff6ff = bg-blue-50  (drop-target fill)
//   #3b82f6 = bg-blue-500 (drop-side indicator)
const DRAG_STYLE_ELEMENT_ID = 'eag-grid-drag-styles';

const ensureDragStyleElement = (): HTMLStyleElement => {
  const existing = document.getElementById(DRAG_STYLE_ELEMENT_ID);
  if (existing instanceof HTMLStyleElement) {
    return existing;
  }
  const el = document.createElement('style');
  el.id = DRAG_STYLE_ELEMENT_ID;
  document.head.appendChild(el);
  return el;
};

const clearDragStyles = () => {
  const el = document.getElementById(DRAG_STYLE_ELEMENT_ID);
  if (el) {
    el.textContent = '';
  }
};

// Selectors target both <td> (body cells) and <th> (header cells) so the
// visual treatment is consistent without HeaderCell needing to subscribe.
const columnSelector = (key: string): string => {
  const escaped = CSS.escape(key);
  return `td[data-column="${escaped}"], th[data-column="${escaped}"]`;
};

// Drop-side indicator box-shadow, insetting from the corresponding edge.
const SIDE_BOX_SHADOW: Record<'left' | 'right', string> = {
  left: 'inset 1px 0 0 #3b82f6',
  right: 'inset -1px 0 0 #3b82f6',
};

// While a drag is in flight the pointer travels over cells that carry their own
// cursor styles (sortable headers: pointer, resize handles: col-resize), so a
// class on the source cell alone can't keep the grabbing cursor visible.
// A global !important rule wins everywhere for the drag's duration; it lives in
// the injected style element, so drop/cancel clears it with the other visuals.
const GRABBING_CURSOR_RULE = '* { cursor: grabbing !important; }';

/** Pure builder of the drag-visual CSS rules — no DOM access, so it is unit-testable. */
const buildDragRules = (
  activeKey: string,
  overKey: string | null,
  side: 'left' | 'right' | null
): string[] => {
  const rules: string[] = [GRABBING_CURSOR_RULE, `${columnSelector(activeKey)} { opacity: 0.3; }`];
  if (overKey != null && overKey !== activeKey) {
    rules.push(`${columnSelector(overKey)} { background-color: #eff6ff; }`);
    if (side === 'left' || side === 'right') {
      rules.push(`${columnSelector(overKey)} { box-shadow: ${SIDE_BOX_SHADOW[side]}; }`);
    }
  }
  return rules;
};

const applyDragStyles = (
  activeKey: string,
  overKey: string | null,
  side: 'left' | 'right' | null
) => {
  ensureDragStyleElement().textContent = buildDragRules(activeKey, overKey, side).join('\n');
};

// Group-header-row cells carry data-group-cell (GroupHeaderCell) so the same
// style-element approach covers them: dimming/highlighting the group <th>
// itself plus every child column's td/th.
const groupCellSelector = (id: string): string => `th[data-group-cell="${CSS.escape(id)}"]`;

const cellColumnKeys = (cell: GroupRowCell): string[] =>
  cell.type === 'group' ? cell.columnKeys : [cell.columnKey];

/** Pure builder of the group-drag CSS rules — mirrors buildDragRules for group cells. */
export const buildGroupDragRules = (
  activeCell: GroupRowCell,
  overCell: GroupRowCell | null,
  side: 'left' | 'right' | null
): string[] => {
  const rules: string[] = [
    GRABBING_CURSOR_RULE,
    `${groupCellSelector(activeCell.id)} { opacity: 0.3; }`,
    ...cellColumnKeys(activeCell).map((key) => `${columnSelector(key)} { opacity: 0.3; }`),
  ];
  if (overCell != null && overCell.id !== activeCell.id) {
    const overKeys = cellColumnKeys(overCell);
    rules.push(`${groupCellSelector(overCell.id)} { background-color: #eff6ff; }`);
    rules.push(...overKeys.map((key) => `${columnSelector(key)} { background-color: #eff6ff; }`));
    if (side === 'left' || side === 'right') {
      const edgeKey = side === 'left' ? overKeys[0] : overKeys.at(-1);
      rules.push(`${groupCellSelector(overCell.id)} { box-shadow: ${SIDE_BOX_SHADOW[side]}; }`);
      if (edgeKey != null) {
        rules.push(`${columnSelector(edgeKey)} { box-shadow: ${SIDE_BOX_SHADOW[side]}; }`);
      }
    }
  }
  return rules;
};

const applyGroupDragStyles = (
  activeCell: GroupRowCell,
  overCell: GroupRowCell | null,
  side: 'left' | 'right' | null
) => {
  ensureDragStyleElement().textContent = buildGroupDragRules(activeCell, overCell, side).join('\n');
};

/**
 * Keeps column drags and group-header drags in separate droppable sets: a
 * column drag never lands on a group-row cell and vice versa, even when the
 * pointer strays into the other header row.
 */
export const kindScopedCollisionDetection: CollisionDetection = (args) => {
  const isGroupDrag = isGroupRowCellId(String(args.active.id));
  return rectIntersection({
    ...args,
    droppableContainers: args.droppableContainers.filter(
      (container) => isGroupRowCellId(String(container.id)) === isGroupDrag
    ),
  });
};

type UseGridDragAndDropParams = {
  visibleColumnStates: EagColumnState[];
  store: EagGridStore;
  reorderByKey: (fromKey: string, toKey: string) => void;
  /** Group-header-row model — null when the group row is not shown (group drags then never occur). */
  groupRowModel: GroupRowModel | null;
};

type UseGridDragAndDropReturn = {
  sensors: ReturnType<typeof useSensors>;
  collisionDetection: CollisionDetection;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragOver: (event: DragOverEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: (event: DragCancelEvent) => void;
};

export const useGridDragAndDrop = (params: UseGridDragAndDropParams): UseGridDragAndDropReturn => {
  // DndContext is owned in EagGridTable (not in HeaderRow) so it renders outside <table>.
  // Placing DndContext inside <table> causes dnd-kit to inject accessibility <div>s
  // as direct children of <table>, which is invalid HTML.
  //
  // Drag state lives in EagGridStore so HeaderCell and DragOverlayColumn can read it
  // directly without prop drilling. GridCell does NOT subscribe — see applyDragStyles above.

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: DRAG_ACTIVATION_DISTANCE}})
  );

  // Resolves both group-drag cells and their render-order side in one place.
  // A stale/unknown id (model rebuilt mid-drag) resolves to null → the caller no-ops.
  const resolveGroupDragTarget = (
    activeId: string,
    overId: string | null
  ): {
    activeCell: GroupRowCell;
    overCell: GroupRowCell | null;
    side: 'left' | 'right' | null;
  } | null => {
    const model = params.groupRowModel;
    const activeCell = model?.cellById.get(activeId);
    if (!model || !activeCell) {
      return null;
    }
    const overCell = overId != null ? (model.cellById.get(overId) ?? null) : null;
    if (!overCell || overCell.id === activeCell.id) {
      return {activeCell, overCell: null, side: null};
    }
    // Side compares render positions of the cells (model order IS render order).
    const side = model.cells.indexOf(activeCell) > model.cells.indexOf(overCell) ? 'left' : 'right';
    return {activeCell, overCell, side};
  };

  const handleDragStart = (event: DragStartEvent) => {
    const activeId = String(event.active.id);
    if (isGroupRowCellId(activeId)) {
      const resolved = resolveGroupDragTarget(activeId, null);
      if (!resolved) {
        return;
      }
      applyGroupDragStyles(resolved.activeCell, null, null);
      params.store.setActiveDragId(activeId);
      return;
    }
    applyDragStyles(activeId, null, null);
    params.store.setActiveDragId(activeId);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const activeId = String(event.active.id);
    const overId = event.over ? String(event.over.id) : null;

    if (isGroupRowCellId(activeId)) {
      const resolved = resolveGroupDragTarget(activeId, overId);
      if (!resolved) {
        return;
      }
      applyGroupDragStyles(resolved.activeCell, resolved.overCell, resolved.side);
      params.store.setDragOver(resolved.overCell?.id ?? null, resolved.side);
      return;
    }

    if (overId == null) {
      applyDragStyles(activeId, null, null);
      params.store.setDragOver(null, null);
      return;
    }

    const activeIndex = params.visibleColumnStates.findIndex((cs) => cs.key === activeId);
    const overIndex = params.visibleColumnStates.findIndex((cs) => cs.key === overId);

    const side = activeIndex > overIndex ? 'left' : 'right';
    applyDragStyles(activeId, overId, side);
    params.store.setDragOver(overId, side);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    clearDragStyles();
    params.store.clearDragState();

    if (!event.over || event.active.id === event.over.id) {
      return;
    }

    const activeId = String(event.active.id);
    if (isGroupRowCellId(activeId)) {
      const resolved = resolveGroupDragTarget(activeId, String(event.over.id));
      if (!resolved || !resolved.overCell || resolved.side == null) {
        return;
      }
      const overKeys = cellColumnKeys(resolved.overCell);
      const targetKey = resolved.side === 'left' ? overKeys[0] : overKeys.at(-1);
      if (targetKey == null) {
        return;
      }
      params.store.reorderColumnBlock(
        cellColumnKeys(resolved.activeCell),
        targetKey,
        resolved.side
      );
      return;
    }
    params.reorderByKey(activeId, String(event.over.id));
  };

  const handleDragCancel = (_event: DragCancelEvent) => {
    clearDragStyles();
    params.store.clearDragState();
  };

  return {
    sensors,
    collisionDetection: kindScopedCollisionDetection,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  };
};
